import React, { Fragment, Suspense, useState } from "react";
import { Helmet } from "react-helmet";
import { Fade } from "react-reveal";
import { useNavigate } from "react-router-dom";

import Loader from "../../components/NewView/Common/Loader";
import Header from "../../components/NewView/Common/Header";
import Footer from "../../components/NewView/Common/Footer";
import IntroSection from "../../components/NewView/Common/IntroSection";
import { useAppDispatch } from "../../hooks/reducer";
import { confirmResetPassword, forgotPassword } from "../../store/slices/auth";

const ForgotPasswordPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [step, setStep] = useState<"telephone" | "code">("telephone");
  const [telephone, setTelephone] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateTelephone = () => {
    const telephoneRegex = /^\+?[1-9]\d{1,14}$/;
    return telephoneRegex.test(telephone);
  };

  const requestCode = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!validateTelephone()) {
      setError("Введіть коректний номер телефону.");
      return;
    }

    try {
      setLoading(true);
      await dispatch(forgotPassword({ telephone }) as any).unwrap();
      setStep("code");
      setMessage("Якщо Telegram прив'язаний до акаунта, код уже надіслано.");
    } catch (err: any) {
      setError(String(err || "Не вдалося надіслати код."));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!code.trim()) {
      setError("Введіть код з Telegram.");
      return;
    }

    if (!/^(?=.*[A-Z]).{8,}$/.test(newPassword)) {
      setError("Новий пароль має бути не менше 8 символів і містити принаймні одну велику літеру.");
      return;
    }

    try {
      setLoading(true);
      await dispatch(
        confirmResetPassword({
          telephone,
          code: code.trim(),
          newPassword,
        }) as any
      ).unwrap();

      setMessage("Пароль успішно змінено. Тепер можна увійти.");
      window.setTimeout(() => navigate("/loginn", { replace: true }), 1200);
    } catch (err: any) {
      setError(String(err || "Невірний або протермінований код."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Suspense fallback={<Loader />}>
        <Helmet>
          <title>YellowHub | Відновлення пароля</title>
        </Helmet>

        <Header />
        <IntroSection title="Відновлення пароля" />

        <div className="loginSection py-[60px] md:py-[80px] lg:py-[100px] bg-gradient-to-br from-gray-100 to-gray-200">
          <div className="container mx-auto">
            <div className="md:mx-[100px] lg:mx-[180px] xl:mx-[300px]">
              <div className="rounded-xl bg-white px-8 py-12 shadow-2xl transition-all duration-300">
                <Fade bottom>
                  <h2 className="text-4xl font-bold font-roboto mb-4 text-gray-900 bg-gradient-to-r from-orange-500 to-orange-700 bg-clip-text text-transparent">
                    Забули пароль?
                  </h2>
                </Fade>

                <p className="font-roboto text-gray-600 mb-8">
                  Введіть номер телефону. Якщо до акаунта прив'язаний Telegram, ми надішлемо код для зміни пароля.
                </p>

                {step === "telephone" ? (
                  <form className="space-y-6" onSubmit={requestCode}>
                    <div>
                      <label className="block text-base font-roboto text-gray-800 mb-2" htmlFor="telephone">
                        Номер телефону:
                      </label>
                      <input
                        id="telephone"
                        type="text"
                        placeholder="+38 (XXX) XXX-XX-XX"
                        className="w-full rounded-xl border border-gray-300 px-6 py-4 font-roboto text-base bg-white shadow-md focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-300"
                        value={telephone}
                        onFocus={() => {
                          if (telephone.trim() === "") {
                            setTelephone("+38");
                          }
                        }}
                        onChange={(e) => setTelephone(e.target.value)}
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full rounded-xl bg-gradient-to-r from-[#F16126] to-orange-600 px-6 py-4 font-roboto text-white font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100"
                      disabled={loading}
                    >
                      {loading ? "Надсилаємо..." : "Надіслати код"}
                    </button>
                  </form>
                ) : (
                  <form className="space-y-6" onSubmit={resetPassword}>
                    <div>
                      <label className="block text-base font-roboto text-gray-800 mb-2" htmlFor="code">
                        Код з Telegram:
                      </label>
                      <input
                        id="code"
                        type="text"
                        inputMode="numeric"
                        placeholder="123456"
                        className="w-full rounded-xl border border-gray-300 px-6 py-4 font-roboto text-base bg-white shadow-md focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-300"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-base font-roboto text-gray-800 mb-2" htmlFor="newPassword">
                        Новий пароль:
                      </label>
                      <input
                        id="newPassword"
                        type="password"
                        placeholder="Новий пароль"
                        className="w-full rounded-xl border border-gray-300 px-6 py-4 font-roboto text-base bg-white shadow-md focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all duration-300"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button
                        type="button"
                        className="rounded-xl border border-gray-300 px-6 py-4 font-roboto text-gray-700 font-bold hover:bg-gray-50 transition-all duration-300"
                        onClick={() => setStep("telephone")}
                      >
                        Назад
                      </button>
                      <button
                        type="submit"
                        className="rounded-xl bg-gradient-to-r from-[#F16126] to-orange-600 px-6 py-4 font-roboto text-white font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-60 disabled:hover:scale-100"
                        disabled={loading}
                      >
                        {loading ? "Перевіряємо..." : "Змінити пароль"}
                      </button>
                    </div>
                  </form>
                )}

                {(message || error) && (
                  <p className={`mt-5 font-roboto text-base ${error ? "text-red-600" : "text-green-700"}`}>
                    {error || message}
                  </p>
                )}

                <button
                  type="button"
                  className="mt-6 font-roboto text-base text-orange-700 hover:text-orange-900 transition-colors duration-300"
                  onClick={() => navigate("/loginn")}
                >
                  Повернутися до входу
                </button>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </Suspense>
    </Fragment>
  );
};

export default ForgotPasswordPage;
